import { Box, Header, Icon, Input, Form, FormField } from '@cloudscape-design/components';
import React, { useState, useEffect } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import '../styles/InviteMemberStyle.css';
import { Role, InviteMemberPayload, OrganizationDetails } from '../types/organizationTypes';
import { organizationService } from 'services/organizationService';
import SubmitButton from 'styles/SubmitButton';
import RoleSelector from 'components/organizations/RoleSelector';
import { isValidEmail } from 'utils/Utils';
import { useNotification } from 'contexts/NotificationContext';
import { useBreadcrumbs } from 'contexts/BreadcrumbContext';
import AppConfig from 'configs/AppConfig';

const InviteMember: React.FC = () => {
  const { addNotification } = useNotification();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { id } = useParams<{ id: string }>();
  const [roles, setRoles] = useState<Role[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<Record<string, boolean>>({});
  const [email, setEmail] = useState('');
  const [loadingRoles, setLoadingRoles] = useState(true);
  const [isInviting, setIsInviting] = useState(false);
  const [emailError, setEmailError] = useState<string | undefined>(undefined);
  const location = useLocation();
  const [organization, setOrganization] = useState<OrganizationDetails | null>(location.state?.organization || null);

  const fetchRoles = async () => {
    try {
      const fetchedRoles = await organizationService.getRealmRoles();

      // Pre-select and disable default roles
      const defaultRoleSelection: Record<string, boolean> = {};
      fetchedRoles.forEach(role => {
        if (role.description === 'default') {
          defaultRoleSelection[role.name] = true;
        }
      });

      setRoles(fetchedRoles);
      setSelectedRoles(defaultRoleSelection);
    } catch (error) {
      throw new Error("Failed to fetch roles");
    } finally {
      setLoadingRoles(false);
    }
  };

  const toggleRole = (role: string): void => {
    setSelectedRoles(prev => ({
      ...prev,
      [role]: !prev[role]
    }));
  };

  const selectAll = (type: 'all' | 'none' | 'all manage' | 'all view' | 'all application'): void => {
    const newRoles = { ...selectedRoles }; // Preserve existing selections

    if (type === 'all') {
      roles.forEach(role => {
        newRoles[role.name] = true;
      });
    } else if (type === 'none') {
      roles.forEach(role => {
        newRoles[role.name] = false;
      });
    } else {
      roles.forEach(role => {
        if (
          (type === 'all manage' && role.name.startsWith('manage-')) ||
          (type === 'all view' && role.name.startsWith('view-')) ||
          (type === 'all application' && role.name.startsWith('manage-clusters'))
        ) {
          newRoles[role.name] = true;
        }
      });
    }

    setSelectedRoles(newRoles);
  };

  const handleSubmit = async (event: CustomEvent) => {
    event.preventDefault();
    setEmailError(undefined);

    if (!email || !isValidEmail(email)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    const selectedRoleNames = Object.keys(selectedRoles).filter(role => selectedRoles[role]);
    setIsInviting(true);

    try {
      const payload: InviteMemberPayload = {
        email,
        roles: selectedRoleNames,
        organizationId: id!,
      };

      await organizationService.inviteMember(payload);
      addNotification('success', 'Invitation sent successfully!');
      setEmail('');
      setSelectedRoles({});
    } catch (error: any) {
      
      if (error.response?.data?.message) {
        setEmailError(error.response?.data?.message);
      } else {
        addNotification('error', 'Failed to send invitation. Please try again.');
      }
    } finally {
      setIsInviting(false);
    }
  };

  // Fetch Organization
  const fetchOrganizationData = async () => {
    if (!id) return;
    try {
      const orgData = await organizationService.getOrganizationById(id);
      setOrganization(orgData);
    } catch (error) {
      console.error("Error fetching organization:", error);
      addNotification("error", "Failed to fetch organization's display name");
    }
  };

  useEffect(() => {
    fetchRoles();

    if(!organization){
      fetchOrganizationData();
    }
    
  }, []);

  useEffect(() => {
    setBreadcrumbs([
      { text: AppConfig.APP_NAME, href: '/' },
      { text: 'Organizations', href: '/organizations' },
      { text: organization?.display_name ?? "organization", href: `/organizations/${id}` },
      { text: 'Invite Member', href: `/organizations/${id}/invite` }
    ]);

    // Cleanup when component unmounts
    return () => {
      setBreadcrumbs([]);
    };
  }, [id, organization?.display_name, setBreadcrumbs]);

  return (
    <div className='invite-member-page'>
      <Box margin={{ bottom: "l" }}>
        <div className="header-container">
          <Icon name="user-profile" size="medium" />

        </div>
      </Box>
      <Box margin={{ bottom: "l" }}>
        <Header variant="h3">Invite new member to <br />
          {organization?.display_name ?? "organization"}
        </Header>
        <Box variant="p">
          Add a new member to the organization by entering their email and assigning them a role within the organization. An email will be sent to them with instructions on how to join.
        </Box>
      </Box>

      <div className="role-selector-wrapper">
        <RoleSelector
          roles={roles}
          selectedRoles={selectedRoles}
          loadingRoles={loadingRoles}
          toggleRole={toggleRole}
          selectAll={selectAll}
          defaultRoles={roles.filter(role => role.description === 'default').map(role => role.name)}
        />
        <br />

        <Form
          actions={
            <SubmitButton
              variant="primary"
              onClick={handleSubmit}
            >
              {isInviting ? "Sending..." : "Send Invitation"}
            </SubmitButton>
          }
        >
          <FormField label="Email" errorText={emailError}>
            <Input
              onChange={({ detail }) => {
                setEmail(detail.value);
                setEmailError(undefined);
              }}
              value={email}
              type="email"
              placeholder="user@domain.com"
              invalid={!!emailError}
            />
          </FormField>
        </Form>
      </div>
    </div>
  );
};

export default InviteMember;
